import React from "react";
import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "./hooks/useTheme";
import { SettingsProvider } from "./context/SettingsContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Program from "./pages/Program";
import Census from "./pages/Census";
import Resources from "./pages/Resources";
import Donate from "./pages/Donate";
import GetInvolved from "./pages/GetInvolved";
import Transparency from "./pages/Transparency";
import Contact from "./pages/Contact";
import HolidayFair from "./pages/HolidayFair";
import Admin from "./pages/Admin";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  componentDidCatch(error) {
    console.error(error);
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-10 text-center">
          <p className="font-display text-2xl font-bold">
            Something went wrong.
          </p>
          <Link to="/" className="btn" data-testid="error-recover-link">
            Back to home
          </Link>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const NotFound = () => (
  <section className="wrap flex min-h-[50vh] flex-col items-start justify-center gap-5 py-20">
    <p className="eyebrow">Page not found</p>
    <h1 className="font-display text-4xl font-extrabold">
      This page has washed out with the tide.
    </h1>
    <p className="max-w-md text-muted-foreground">
      The link may be old or mistyped. Head back home and we will point you
      the right way.
    </p>
    <Link to="/" className="btn" data-testid="notfound-home-link">
      Back to home
    </Link>
  </section>
);

function RoutedApp() {
  const { theme } = useTheme();
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/program" element={<Program />} />
            <Route path="/census" element={<Census />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/get-involved" element={<GetInvolved />} />
            <Route path="/transparency" element={<Transparency />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/holiday-fair" element={<HolidayFair />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <Toaster position="bottom-right" theme={theme} richColors closeButton />
    </BrowserRouter>
  );
}

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <SettingsProvider>
          <RoutedApp />
        </SettingsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
